/**
 * agent-config.ts — Phase B pilot AgentConfig for cogo-starter.
 *
 * Consumed by:
 *   - <CogoAgent /> (starter-level UI panel) — persona badge, confirmation UI
 *   - <WebMCPProvider /> wrap in src/main.tsx (registry population)
 *
 * Keeps all Phase B pilot knobs in one place so Phase C apps can replicate
 * the pattern by copy + tweak (no runtime changes).
 */

import {
  requiresConfirmation as runtimeRequiresConfirmation,
  type WebMCPTool,
  type ToolManifestEntry,
} from "@cogo/webmcp-runtime"

// ── Types ───────────────────────────────────────────────────

/**
 * A single tool-call the agent proposes to execute.
 * Shape is independent of any specific LLM provider — adapters map their
 * provider-specific function-call payload onto this.
 */
export interface ProposedToolCall {
  /** Stable id for this proposal (used to resolve the confirmation promise). */
  id: string
  /** Tool manifest entry from the registry. */
  tool: ToolManifestEntry
  /** Resolved arguments from the LLM. */
  args: Record<string, unknown>
}

/** Confirmation verdict returned by the UI. */
export type ConfirmationPolicyVerdict = "auto" | "confirm"

/**
 * LLM adapter interface — smallest surface that lets the starter-level
 * CogoAgent render tool-call previews without taking a hard dep on any
 * specific LLM SDK.
 *
 * Runtime contract:
 *   - sendMessage(user) resolves with 0+ proposed tool calls + an optional
 *     assistant text reply. The caller (<CogoAgent>) decides whether each
 *     proposal auto-executes or pops a confirmation.
 *   - Adapters may pull tool manifest / narrative context from the WebMCP
 *     registry injected at construction time.
 */
export interface LlmAdapter {
  /** Human-readable id shown in the agent UI footer. */
  readonly id: string
  /**
   * Send a user message and return proposed tool calls + optional reply.
   * Errors should be thrown so the panel can surface them.
   */
  sendMessage(params: {
    userMessage: string
    tools: ToolManifestEntry[]
  }): Promise<{ reply?: string; toolCalls: ProposedToolCall[] }>
}

/** Phase B pilot configuration. */
export interface AgentConfig {
  persona: "shopping" | "dashboard" | "generic" | "partner"
  /**
   * Decide whether a proposed tool call must be confirmed by the user.
   * Returning "confirm" shows the approve/reject UI; "auto" runs immediately.
   */
  confirmationPolicy(call: ProposedToolCall): ConfirmationPolicyVerdict
  /** LLM adapter resolver. Allows lazy init + env-based fallback. */
  llmAdapter: LlmAdapter
}

// ── Default confirmation policy ─────────────────────────────

/**
 * Default Phase B confirmation policy:
 *   1. If the tool metadata says `readOnly: true` -> auto.
 *   2. If the runtime's `requiresConfirmation` heuristic fires
 *      (destructive name pattern, `crud.delete`/`checkout`/`payment` tags)
 *      -> confirm.
 *   3. Otherwise anything that *writes* (flow tools are `readOnly: false`)
 *      -> confirm. The pilot optimizes for user trust, not throughput.
 */
export function defaultConfirmationPolicy(
  call: ProposedToolCall,
): ConfirmationPolicyVerdict {
  const { tool } = call

  if (tool.metadata?.readOnly) return "auto"

  // Adapt the ToolManifestEntry to the WebMCPTool shape `requiresConfirmation`
  // expects (it only reads name + metadata; handler is irrelevant here).
  const adapted: WebMCPTool = {
    name: tool.name,
    description: tool.description,
    inputSchema: tool.inputSchema,
    outputSchema: tool.outputSchema,
    metadata: tool.metadata,
    handler: async () => undefined,
  }
  if (runtimeRequiresConfirmation(adapted, call.args)) return "confirm"

  // Flow-tagged tools mutate state; err on the side of confirm in Phase B.
  if (tool.metadata?.tags?.includes("flow")) return "confirm"
  return "auto"
}

// ── LLM adapters ────────────────────────────────────────────

/**
 * Max wall-clock a single Gemini request is allowed to hang before we
 * abort it. Protects the agent UI from indefinite "busy" states on
 * network brownouts, key quota stalls, or regional outages — without
 * one, sendMessage() would never resolve and the composer would stay
 * disabled forever.
 */
const GEMINI_TIMEOUT_MS = 30_000

/**
 * Echo adapter — used when VITE_GEMINI_API_KEY is absent.
 * Picks the first flow-tagged tool that matches any keyword in the user
 * message; otherwise returns an "I don't know that yet" reply. Gives E2E
 * smoke tests a deterministic, offline-capable path.
 */
export function createEchoAdapter(): LlmAdapter {
  return {
    id: "echo-adapter",
    async sendMessage({ userMessage, tools }) {
      const lower = userMessage.toLowerCase()
      const flowTools = tools.filter((t) => t.metadata?.tags?.includes("flow"))

      // Keyword-based match against tool names.
      const keywords = lower.split(/[^a-z0-9]+/).filter(Boolean)
      const scored = flowTools.map((t) => {
        const name = t.name.toLowerCase()
        const desc = t.description.toLowerCase()
        let score = 0
        for (const kw of keywords) {
          if (kw.length < 3) continue
          if (name.includes(kw)) score += 3
          if (desc.includes(kw)) score += 1
        }
        return { tool: t, score }
      })
      scored.sort((a, b) => b.score - a.score)

      const best = scored[0]
      if (!best || best.score === 0) {
        return {
          reply:
            "I'd need a Gemini key to interpret that. Try a phrase matching a tool name, e.g. \"clear cart\" or \"remove item\".",
          toolCalls: [],
        }
      }

      const call: ProposedToolCall = {
        id: `echo-${Date.now()}`,
        tool: best.tool,
        args: {},
      }
      return {
        reply: `Proposing tool "${best.tool.name}" based on keyword match.`,
        toolCalls: [call],
      }
    },
  }
}

/**
 * Gemini adapter — thin wrapper over generativelanguage.googleapis.com REST.
 * We intentionally avoid the full GeminiAgentRuntime orchestration here;
 * the starter UI needs only the *proposal* step (what the model wants to
 * call). Execution + confirmation are handled by <CogoAgent /> itself so
 * the user stays in control.
 */
export function createGeminiAdapter(apiKey: string, model = "gemini-2.5-flash"): LlmAdapter {
  const api = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`
  return {
    id: `gemini:${model}`,
    async sendMessage({ userMessage, tools }) {
      const functionDeclarations = tools.map((t) => ({
        name: t.name,
        description: t.description,
        parameters: t.inputSchema,
      }))

      const body = {
        system_instruction: {
          parts: [
            {
              text:
                "You are a shopping assistant. Use the declared tools to fulfill user requests. " +
                "Prefer a single tool call per turn. Always be concise.",
            },
          ],
        },
        contents: [{ role: "user", parts: [{ text: userMessage }] }],
        tools: functionDeclarations.length > 0 ? [{ functionDeclarations }] : undefined,
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), GEMINI_TIMEOUT_MS)
      try {
        const res = await fetch(`${api}?key=${encodeURIComponent(apiKey)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: controller.signal,
        })
        if (!res.ok) {
          throw new Error(`Gemini API error ${res.status}`)
        }
        const json = (await res.json()) as {
          candidates?: Array<{
            content?: {
              parts?: Array<{
                text?: string
                functionCall?: { name: string; args?: Record<string, unknown> }
              }>
            }
          }>
        }

        const parts = json.candidates?.[0]?.content?.parts ?? []
        const toolCalls: ProposedToolCall[] = []
        let reply: string | undefined
        for (const part of parts) {
          if (part.functionCall) {
            const fc = part.functionCall
            const match = tools.find((t) => t.name === fc.name)
            if (!match) continue
            toolCalls.push({
              id: `gemini-${Date.now()}-${toolCalls.length}`,
              tool: match,
              args: fc.args ?? {},
            })
          } else if (part.text) {
            reply = (reply ?? "") + part.text
          }
        }
        return { reply, toolCalls }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          throw new Error(`Gemini request timed out after ${GEMINI_TIMEOUT_MS}ms`)
        }
        throw err
      } finally {
        clearTimeout(timeoutId)
      }
    },
  }
}

/** Build the adapter used by the Phase B pilot. */
export function resolveLlmAdapter(): LlmAdapter {
  const key =
    typeof import.meta !== "undefined" && (import.meta as ImportMeta).env?.VITE_GEMINI_API_KEY
      ? String((import.meta as ImportMeta).env.VITE_GEMINI_API_KEY)
      : ""
  if (!key) return createEchoAdapter()
  return createGeminiAdapter(key)
}

// ── Exported config instance ────────────────────────────────

export const STARTER_AGENT_CONFIG: AgentConfig = {
  persona: "shopping",
  confirmationPolicy: defaultConfirmationPolicy,
  llmAdapter: resolveLlmAdapter(),
}
